<template>
  <div class="success-status__card">
    <div class="success-status__header">
      <img src="@/assets/email.svg" alt="Email icon" class="success-status__icon" />
      <h2 class="success-status__title">
        {{ title }}
      </h2>
      <p class="success-status__description">
        {{ description }}
      </p>
      <p class="success-status__email">
        {{ email }}
      </p>
    </div>

    <div class="success-status__footer">
      <hr class="divider" />
      <p class="success-status__note">
        {{ emailNote }}
      </p>
      <button
        class="success-status__back-btn"
        type="button"
        @click="onBackClick"
      >
        {{ backLabel }}
      </button>
    </div>
    <SupportButton :label="supportLabel" />
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

import TrackingService from '@/services/TrackingService'
import { ActionType, SourceObjectKey, SourceObjectValueElementSubtype, EventSubtypes } from '@/types/events'

import SupportButton from './SupportButton.vue'

const emit = defineEmits(['back'])

withDefaults(defineProps<{
  email: string
  title?: string
  description?: string
  emailNote?: string
  backLabel?: string
  supportLabel?: string
}>(), {
  title: 'Thank you!',
  description: 'Within 3 days, we\'ll respond to your request via email at:',
  emailNote: 'Wrong email address?',
  backLabel: 'Send Request Again',
  supportLabel: 'Instant Support',
})

function onBackClick(): void {
  emit('back')

  TrackingService.trackComponentInteraction(
    ActionType.Click,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.SendRequestAgain,
      },
    ],
    EventSubtypes.DescriptorPage,
  )
}

onMounted((): void => {
  TrackingService.trackComponentInteraction(
    ActionType.Show,
    [
      {
        key: SourceObjectKey.ElementSubtype,
        value: SourceObjectValueElementSubtype.ThankYouScreen,
      },
    ],
    EventSubtypes.DescriptorPage,
  )
})
</script>

<style scoped lang="scss">
.success-status {
  &__card {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    margin: 0;
    padding: 32px 40px;
    border-radius: 12px;
    background-color: var(--color-bg-form);
    gap: 32px;
  }

  &__icon {
    color: var(--color-primary);
  }

  &__header {
    text-align: center;
  }

  &__title {
    margin-top: 25px;
    margin-bottom: 8px;
    color: var(--color-text);
    font-size: 24px;
    font-weight: var(--font-weight-semibold);
  }

  &__description {
    margin-bottom: 24px;
    color: var(--color-text);
    font-size: 16px;
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height);
  }

  &__email {
    margin-bottom: 0;
    font-size: 20px;
    font-weight: var(--font-weight-semibold);
  }

  &__footer {
    width: 100%;
    text-align: center;
  }

  &__note {
    margin-top: 32px;
    margin-bottom: 8px;
    color: var(--color-text);
    font-size: 16px;
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height);
  }

  &__back-btn {
    padding: 0;
    cursor: pointer;
    transition: opacity 0.2s ease;
    text-decoration: underline;
    color: var(--color-primary);
    border: none;
    outline-offset: 2px;
    background: none;
    font-size: 16px;
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height);

    &:hover {
      opacity: 0.8;
    }

    &:focus {
      border-radius: 4px;
      outline: 2px solid #4a9b8e;
      outline-offset: 2px;
    }
  }
}

.divider {
  width: 100%;
  height: 1px;
  margin: 0;
  border: none;
  background-color: #43977e66;
}
</style>
